import { ChangeDetectorRef, Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { UserSupportService } from './services/user-support.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { MeetingData, UserQueue } from './models/userQueue';
import { HttpClient } from '@angular/common/http';
import { MeetingRoomComponent } from '../meeting-room/meeting-room.component';
import { AttendantStatusService } from './services/attendant-status.service';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { JitsiMeetingComponent } from '../jitsi-meeting/jitsi-meeting.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EMessageType, ResponseDefault } from 'src/app/shared/data/response-default.interface';
import { FilasService } from '../filas/services/filas.service';
import { IFila } from '../filas/data/lista.interface';

@Component({
  selector: 'app-operator-management-room',
  templateUrl: './user-support.component.html',
  styleUrls: ['./user-support.component.scss']
})
export class UserSupportComponent implements OnInit, AfterViewInit {
  @ViewChild(MeetingRoomComponent, { static: false }) meetingRoomComponent!: MeetingRoomComponent;
  @ViewChild(JitsiMeetingComponent, { static: false }) JitsiMeetingComponent!: JitsiMeetingComponent;
  operatorId!: string;
  token!: string;
  zoomURL = '';
  operatorFullName!: string;
  queueArray: UserQueue[] = [];
  openFormServiceInformation = false;
  appointmentId = '';
  protocol = '';
  ERoleAdminString = ERoleAdminString;

  currentUser: UserQueue | null = null;
  isAvailable = false;

  minhasFilas: IFila[] = [];
  carregandoFilas = false;
  filasExpanded = false;

  private readonly audio = new Audio();
  private readonly audioKey = 'audioPlayed';

  constructor(
    private readonly operatorWebSocketService: UserSupportService,
    private readonly sessionService: SessionService,
    private readonly changeDetectorRef: ChangeDetectorRef, // Inject ChangeDetectorRef
    public httpClient: HttpClient,
    private readonly attendantStatusService: AttendantStatusService,
    private readonly toastService: ToastService,
    private readonly filasService: FilasService
  ) {
    this.audio.src = 'assets/sounds/livechat.mp3';
    this.audio.load();
  }

  ngAfterViewInit(): void {
    if (this.meetingRoomComponent) {
      this.meetingRoomComponent.closeMeetingEvent.subscribe(() => {
        this.finishChat();
      });
    }

    if (this.JitsiMeetingComponent) {
      this.JitsiMeetingComponent.closeMeetingEvent.subscribe(() => {
        this.finishChat();
      });
    }
  }

  public ngOnInit(): void {
    const sessionUser = this.sessionService.getSession();

    this.operatorId = sessionUser.sid;
    this.token = this.sessionService.getToken();
    this.operatorFullName = sessionUser.name;

    this.carregarMinhasFilas();
    this.getAttendantStatus();
  }

  private carregarMinhasFilas(): void {
    this.carregandoFilas = true;

    this.filasService.listarFilasPorAtendente(this.operatorId).subscribe({
      next: (resposta) => {
        this.minhasFilas = resposta?.data ?? [];
        this.carregandoFilas = false;
      },
      error: () => {
        this.minhasFilas = [];
        this.carregandoFilas = false;
      }
    });
  }

  async onClick(userId?: string, meetingData?: MeetingData | null, meetingUrl?: string | null): Promise<void> {
    sessionStorage.setItem(this.audioKey, 'true');
    const normalizedProvider = meetingData?.meetingProvider?.trim().toLowerCase();
    const hasMeetingUrl = !!meetingUrl?.trim();
    const hasJitsiMetadata = !!meetingData?.meetingRoom?.trim() && !!meetingData?.meetingDomain?.trim();
    const shouldStartJitsi = normalizedProvider === 'jitsi' && hasJitsiMetadata;
    const shouldStartLegacy = !shouldStartJitsi && hasMeetingUrl;

    if (userId && (shouldStartJitsi || shouldStartLegacy)) {
      try {
        await this.startMeeting(meetingData, meetingUrl);
        const message = {
          type: 'openChatWindow',
          userId: userId,
          operatorId: this.operatorId
        };
        this.operatorWebSocketService.sendMessage(message);
        const user = this.queueArray.find((queue) => queue.user.id === userId);

        if (user) {
          this.currentUser = user;
          this.saveDataSessionStorage(user.appointmentId, user.protocol);
        }
      } catch (error) {
        console.error('error when trying open', error);
      }
    }
  }

  private playNotificationSound(): void {
    this.audio.load();
    this.audio.play();
  }

  private getAttendantStatus(): void {
    this.attendantStatusService.getStatus().subscribe((response) => {
      if (response.isSucceed) {
        this.isAvailable = response.data.status === 'disponível';

        sessionStorage.setItem('isAvailable', this.isAvailable.toString());
        if (this.isAvailable) {
          this.connectWebSocket();
        } else {
          this.disconnectWebSocket();
        }
      }
    });
  }

  private async startMeeting(meetingData?: MeetingData | null, meetingUrl?: string | null): Promise<void> {
    const provider = meetingData?.meetingProvider?.trim().toLowerCase();

    if (provider === 'jitsi') {
      const meetingDomain = meetingData?.meetingDomain?.trim();
      const meetingRoom = meetingData?.meetingRoom?.trim();

      if (!meetingDomain || !meetingRoom) {
        throw new Error('Meeting domain and room are required for Jitsi meetings.');
      }

      await this.JitsiMeetingComponent.startMeeting(meetingDomain, meetingRoom);
      return;
    }

    if (meetingUrl?.trim()) {
      await this.meetingRoomComponent.startMeeting(meetingUrl.trim());
      return;
    }

    throw new Error(`No meeting data available for provider: ${provider ?? 'legacy'}`);
  }

  /** Painel de dados do paciente só aparece se a fila dele estiver configurada para isso. */
  get showPatientData(): boolean {
    const queueId = this.currentUser?.queueId;
    return !!queueId && this.minhasFilas.some((fila) => fila.id === queueId && fila.showPatientData);
  }

  finishChat(): void {
    const userId = this.currentUser?.user.id;
    if (!userId) {
      return;
    }
    this.operatorWebSocketService.sendFinishChat(userId, this.operatorId);
  }

  saveDataSessionStorage(appointmentId: string, protocol: string): void {
    sessionStorage.setItem('openFormServiceInformation', 'true');
    sessionStorage.setItem('appointmentId', appointmentId || '');
    sessionStorage.setItem('protocol', protocol || '');
  }

  toggleState(): void {
    const newStatus = !this.isAvailable;

    const changeStatus = {
      status: newStatus ? 'disponível' : 'indisponível'
    };

    this.attendantStatusService.changeStatus(changeStatus).subscribe({
      next: (response) => {
        if (response.isSucceed) {
          this.applyAttendantStatus(newStatus);
        } else {
          this.toastService.toast(this.extractErrorMessage(response), EMessageType.Error);
        }
      },
      error: (error) => {
        this.toastService.toast(this.extractErrorMessage(error?.error), EMessageType.Error);
      }
    });
  }

  private applyAttendantStatus(isAvailable: boolean): void {
    this.isAvailable = isAvailable;
    sessionStorage.setItem('isAvailable', this.isAvailable.toString());

    if (this.isAvailable) {
      this.connectWebSocket();
    } else {
      this.disconnectWebSocket();
    }
  }

  private extractErrorMessage(response?: ResponseDefault<unknown>): string {
    return response?.messages?.[0]?.description ?? 'Erro ao trocar status. Tente novamente.';
  }

  connectWebSocket(): void {
    console.log('connectWebSocket');
    this.operatorWebSocketService.connectWebSocket(this.operatorId);

    this.operatorWebSocketService.getUserQueue().subscribe((userQueue) => {
      const audio = sessionStorage.getItem(this.audioKey);

      if (userQueue.length > 0 && userQueue.length > this.queueArray.length && !audio) {
        this.playNotificationSound();
      }

      this.queueArray = userQueue;
      sessionStorage.removeItem(this.audioKey);

      this.changeDetectorRef.detectChanges();
    });
  }

  disconnectWebSocket(): void {
    console.log('disconnectWebSocket');
    this.operatorWebSocketService.disconnectWebSocket();

    this.queueArray = [];
  }
}
